import { useState, useCallback, useRef } from "react";
import { CustomModal, Button } from "../../shared";
import { HiOutlineFolder, HiOutlineChevronRight, HiOutlineCloudArrowUp } from "react-icons/hi2";
import { cn } from "../../../lib/cn";

const FolderTreeNode = ({ node, depth = 0, selectedFolderId, onSelectFolder }) => {
  const [expanded, setExpanded] = useState(depth === 0);
  const children = node.children || [];
  const hasChildren = children.length > 0;
  const isSelected = selectedFolderId === node.id;

  return (
    <div>
      <div
        className={cn(
          "flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-surface-hover",
          isSelected && "bg-secondary/10 ring-1 ring-secondary/40",
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        <button
          type="button"
          onClick={() => hasChildren && setExpanded((v) => !v)}
          className={cn(
            "flex h-5 w-5 items-center justify-center rounded text-grey transition-transform",
            hasChildren ? "opacity-100" : "invisible",
            expanded && "rotate-90",
          )}
          aria-label={expanded ? "Collapse folder" : "Expand folder"}
          aria-expanded={expanded}
        >
          <HiOutlineChevronRight className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onSelectFolder(node.id, node.name)}
          className="flex flex-1 items-center gap-2 text-left"
          aria-pressed={isSelected}
        >
          <HiOutlineFolder className={cn("h-4 w-4", isSelected ? "text-secondary-dark" : "text-grey")} />
          <span className={cn("truncate font-medium", isSelected ? "text-secondary-dark" : "text-darkgrey")}>
            {node.name}
          </span>
        </button>
      </div>

      {expanded && hasChildren && (
        <div className="ml-3 border-l border-surface-border pl-1">
          {children.map((child) => (
            <FolderTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedFolderId={selectedFolderId}
              onSelectFolder={onSelectFolder}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FileUploadAndAttachModal = ({
  open,
  onClose,
  onUpload,
  isLoading = false,
  objectType = "Task",
  folderTree,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [selectedFolderName, setSelectedFolderName] = useState("Project root");
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const folderNodes = folderTree?.children || [];

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const dt = e.dataTransfer;
    const files = dt?.files;
    if (files) {
      setSelectedFiles(Array.from(files));
      setError("");
    }
  }, []);

  const handleFileSelect = (e) => {
    setSelectedFiles(Array.from(e.target.files || []));
    setError("");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleSubmit = () => {
    if (!selectedFiles.length) {
      setError("Select at least one file.");
      return;
    }
    onUpload?.({
      files: selectedFiles,
      folderId: selectedFolderId,
    });
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setSelectedFolderId(null);
    setSelectedFolderName("Project root");
    setError("");
    onClose?.();
  };

  return (
    <CustomModal
      title={`Upload & attach to ${objectType}`}
      open={open}
      close={handleClose}
      showPrimaryButton={false}
      showSecondaryButton={false}
      dialogSize="lg"
    >
      <div className="flex flex-col gap-4">
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className={cn(
            "flex flex-col items-center gap-3 rounded-xl border-2 border-dashed p-8 text-center transition-colors",
            selectedFiles.length > 0
              ? "border-green/40 bg-green/[0.04]"
              : "border-surface-border hover:border-secondary/40",
          )}
        >
          <HiOutlineCloudArrowUp className="h-10 w-10 text-grey" />
          <div>
            <p className="text-sm font-medium text-darkgrey">
              {selectedFiles.length > 0
                ? `${selectedFiles.length} file(s) selected`
                : "Drag & drop files here"}
            </p>
            <p className="mt-1 text-xs text-grey">
              or{" "}
              <button
                type="button"
                className="text-secondary-dark underline"
                onClick={() => fileInputRef.current?.click()}
              >
                browse files
              </button>
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-darkgrey">Destination folder</p>
            <span className="text-xs text-grey">{selectedFolderName}</span>
          </div>
          <div className="max-h-48 overflow-y-auto rounded-xl border border-surface-border bg-white p-2">
            <button
              type="button"
              onClick={() => {
                setSelectedFolderId(null);
                setSelectedFolderName("Project root");
              }}
              className={cn(
                "mb-1 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-surface-hover",
                selectedFolderId === null && "bg-secondary/10 ring-1 ring-secondary/40",
              )}
            >
              <HiOutlineFolder className={cn("h-4 w-4", selectedFolderId === null ? "text-secondary-dark" : "text-grey")} />
              <span className={cn("truncate font-medium", selectedFolderId === null ? "text-secondary-dark" : "text-darkgrey")}>
                Project root
              </span>
            </button>

            {folderNodes.length > 0 ? (
              folderNodes.map((node) => (
                <FolderTreeNode
                  key={node.id}
                  node={node}
                  selectedFolderId={selectedFolderId}
                  onSelectFolder={(folderId, folderName) => {
                    setSelectedFolderId(folderId);
                    setSelectedFolderName(folderName);
                  }}
                />
              ))
            ) : (
              <p className="px-2 py-3 text-sm text-grey">No subfolders yet.</p>
            )}
          </div>
        </div>

        {selectedFiles.length > 0 && (
          <div className="max-h-32 overflow-y-auto rounded-lg border border-surface-border p-2">
            {selectedFiles.map((f, i) => (
              <div key={i} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm">
                <HiOutlineCloudArrowUp className="h-4 w-4 shrink-0 text-grey" />
                <span className="truncate text-darkgrey">{f.name}</span>
                <span className="shrink-0 text-xs text-grey">
                  {(f.size / 1024 / 1024).toFixed(1)} MB
                </span>
                <button
                  type="button"
                  className="ml-auto text-xs text-red hover:underline"
                  onClick={() => setSelectedFiles((prev) => prev.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {error && <p className="text-sm text-red">{error}</p>}

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="filled"
            color="primary"
            onClick={handleSubmit}
            loading={isLoading}
            disabled={!selectedFiles.length || isLoading}
          >
            Upload & attach
          </Button>
        </div>
      </div>
    </CustomModal>
  );
};

export default FileUploadAndAttachModal;
